export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a Date object to YYYY-MM-DD string.
 * @param {Date} date 
 * @returns {string}
 */
export const formatDateToYYYYMMDD = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculates Date of Birth (YYYY-MM-DD) from age (years, months, days).
 * @param {number|string} years 
 * @param {number|string} months 
 * @param {number|string} days 
 * @returns {string}
 */
export const calculateDobFromAge = (years, months, days) => {
  const today = new Date();
  const birthDate = new Date(today);
  
  birthDate.setFullYear(today.getFullYear() - (parseInt(years) || 0));
  birthDate.setMonth(today.getMonth() - (parseInt(months) || 0));
  birthDate.setDate(today.getDate() - (parseInt(days) || 0));

  return formatDateToYYYYMMDD(birthDate);
};

/**
 * Calculates age (years, months, days) from Date of Birth string.
 * @param {string} dob - YYYY-MM-DD format
 * @returns {{years: number|string, months: number|string, days: number|string}}
 */
export const calculateAgeFromDob = (dob) => {
  if (!dob) return { years: '', months: '', days: '' };
  const birthDate = new Date(dob);
  const today = new Date();
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    months--;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  return {
    years: years > 0 ? years : '',
    months: months > 0 ? months : '',
    days: days > 0 ? days : '',
  };
};

/**
 * Formats a Date object for selected display: "DayName, DD MonthName YYYY"
 * @param {Date} date 
 * @returns {string}
 */
export const formatSelectedDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  return `${DAY_NAMES[date.getDay()]}, ${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Converts 12h time string (e.g., "10:00 AM") to 24h format (e.g., "10:00:00")
 * @param {string} timeStr 
 * @returns {string}
 */
export const formatTimeTo24h = (timeStr) => {
  if (!timeStr) return '';
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') {
    hours = modifier === 'AM' ? '00' : '12';
  } else if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }
  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
};

/**
 * Creates a day object with metadata for scheduling.
 * @param {Date} date 
 * @param {Array} slots 
 * @returns {Object}
 */
export const createDayObject = (date, slots = []) => {
  const idx = date.getDay();
  return {
    date: new Date(date),
    dayName: DAY_NAMES[idx],
    monthName: MONTH_NAMES[date.getMonth()],
    hasSlot: slots.length > 0,
    slots,
  };
};

/**
 * Generates an array of 7 days for a given week offset.
 * @param {number} weekOffset 
 * @param {Object} slotsByDayIndex - Map of day index (0-6) to slots array
 * @returns {Array}
 */
export const getWeekDays = (weekOffset, slotsByDayIndex = {}) => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - today.getDay() + weekOffset * 7);
  start.setHours(0, 0, 0, 0);
  
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const slots = slotsByDayIndex[d.getDay()] || [];
    return createDayObject(d, slots);
  });
};
